/**
 * ─── THE PROFFPLASSEN MARK ──────────────────────────────────────────────────
 *
 * ⚠️ THIS FILE IS THE ONLY PLACE THE MARK'S GEOMETRY AND COLOURS EXIST.
 *
 * The web lockup, the admin lockup, the mobile lockup, the browser favicon, the
 * web-app-manifest icons, the iOS/Android app icon, the Android adaptive icon
 * and the splash screen are all rendered from the four numbers and six hex
 * values below. Changing the mark is an edit to THIS file plus a re-run of
 * `node packages/config/scripts/gen-icons.mjs` for the rasters — it is deliberately not a
 * hunt through twenty components.
 *
 * ─── WHERE THE VALUES CAME FROM ─────────────────────────────────────────────
 *
 * The client sent finished artwork on 6 October 2026, as two JPEGs:
 * `Images/Logo/logo-light.jpeg` and `Images/Logo/logo-dark.jpeg`, 1254 × 1254.
 *
 * Every colour below was SAMPLED from those files with System.Drawing, not
 * eyeballed. The geometry was measured the same way — area-based radii and
 * run-length scanlines through the circle centres — and then normalised,
 * because the two files do not actually agree with each other:
 *
 *   file    r(left)   r(right)   r2/r1    dx/r1    union aspect
 *   light    255.8     204.2     0.798    1.123       1.461
 *   dark     214.2     189.0     0.882    1.179       1.508
 *   ours      26         22      0.846    1.115       1.481   ← the average
 *
 * They are AI-generated boards rather than a drawn identity, so there is no
 * "correct" answer hiding in them to recover; the midpoint is the honest
 * reading and it sits inside the disagreement on every measure.
 *
 * ⚠️ JPEG HAS NO ALPHA, which is why the mark is redrawn here rather than
 * shipped as the supplied files. `logo-light.jpeg` placed on any coloured
 * surface is a white box with a logo in it, and a JPEG cannot be a favicon, an
 * iOS icon or an Android adaptive foreground at all.
 *
 * ─── ⚠️ THE MASTERCARD RESEMBLANCE ──────────────────────────────────────────
 *
 * Two overlapping circles with a lighter intersection is structurally the
 * Mastercard mark, and this product is adding a payment feature — which is the
 * context in which that resemblance stops being a coincidence and starts being
 * a trademark question. It has been raised with the client. It is HIS call, not
 * ours, so the mark is built faithfully as sent.
 *
 * The practical consequence is this file's shape: if the geometry has to change
 * later (separate the circles, drop the intersection tint, go to one circle),
 * it is `logoGeometry` below and nothing else. No component knows a coordinate.
 *
 * ─── THE WORDMARK IS SENTENCE CASE ──────────────────────────────────────────
 *
 * Both supplied files set it as "Proffplassen" — one capital. The onboarding
 * mockups from September set the OLD name in capitals ("Join AVEXA"), and
 * `brandInterpolation.productUpper` still renders `PROFFPLASSEN` for that copy.
 * The lockups here follow the ARTWORK, because the artwork is the logo and the
 * copy is not. See the report note on `{{productUpper}}`.
 */

/**
 * Two circles, in a 77 × 52 viewBox that is exactly their union's bounding box
 * — so the mark has no built-in padding and every consumer controls its own.
 *
 * left:  cx 26, cy 26, r 26  → spans x 0..52,  y 0..52
 * right: cx 55, cy 30, r 22  → spans x 33..77, y 8..52
 */
export const logoGeometry = {
  width: 77,
  height: 52,
  left: { cx: 26, cy: 26, r: 26 },
  right: { cx: 55, cy: 30, r: 22 },
} as const

/**
 * The two colourways, sampled from the two supplied files.
 *
 * `primary` is the large left circle, `secondary` the smaller right one, and
 * `overlap` the intersection. `ground` is the background the file was drawn on
 * — it is the app-icon and splash fill, not a UI surface token.
 *
 * ⚠️ `dark.secondary` (`#097EFF`) is NOT the UI action colour. White on it is
 * 3.86:1, which fails WCAG AA for text. `tokens.ts` keeps `blue[500]` at
 * `#006DFD` (4.56:1) for that reason — the logo's blue is a logo blue.
 */
export const logoColors = {
  /** For a light ground: navy + pale blue, as `logo-light.jpeg`. */
  light: {
    primary: '#022754',
    secondary: '#C3E1FD',
    overlap: '#1E87FD',
    ground: '#FEFEFE',
    wordmark: '#022754',
  },
  /** For a dark ground: white + bright blue, as `logo-dark.jpeg`. */
  dark: {
    primary: '#FFFFFF',
    secondary: '#097EFF',
    overlap: '#BDDEFD',
    ground: '#012C63',
    wordmark: '#FFFFFF',
  },
} as const

export type LogoColorway = keyof typeof logoColors
export type LogoPalette = (typeof logoColors)[LogoColorway]

/**
 * Proportion of a square tile taken up by the mark's WIDTH.
 *
 * 0.68 on a plain tile (favicon, iOS icon — iOS applies its own mask, so the
 * corners are not ours to protect) and 0.56 on an Android adaptive foreground,
 * where the launcher may crop to a circle inscribed in the middle 66 % and
 * anything outside that can be shaved off.
 */
export const markTileScale = { tile: 0.68, adaptive: 0.56 } as const

// ─── SVG emitters ───────────────────────────────────────────────────────────
//
// Strings rather than components, because the consumers are not all React: the
// Vite plugins write these straight into `dist/`, and the icon generator
// rasterises them. The React lockups draw the same two circles from
// `logoGeometry` directly, so there is still only one set of numbers.

const { width, height, left, right } = logoGeometry

/**
 * The mark's shapes, with no `<svg>` wrapper and no transform — ready to drop
 * inside an existing `<svg>` or `<g>`.
 *
 * The intersection is drawn as the RIGHT circle clipped to the LEFT one. The
 * alternative is an analytically derived lens path, which is shorter but has to
 * be recomputed by hand the moment a radius moves — exactly the trap this file
 * exists to avoid.
 *
 * `id` namespaces the `clipPath`, because two lockups on one page would
 * otherwise declare the same id twice.
 */
export const markShapes = (palette: LogoPalette, id: string): string =>
  [
    `<defs><clipPath id="${id}-clip">`,
    `<circle cx="${left.cx}" cy="${left.cy}" r="${left.r}"/>`,
    '</clipPath></defs>',
    `<circle cx="${left.cx}" cy="${left.cy}" r="${left.r}" fill="${palette.primary}"/>`,
    `<circle cx="${right.cx}" cy="${right.cy}" r="${right.r}" fill="${palette.secondary}"/>`,
    `<circle cx="${right.cx}" cy="${right.cy}" r="${right.r}" fill="${palette.overlap}"`,
    ` clip-path="url(#${id}-clip)"/>`,
  ].join('')

type MarkSvgOptions = {
  colorway?: LogoColorway
  /** Accessible name. Pass `null` for a purely decorative mark. */
  label?: string | null
  id?: string
}

/** The mark alone, on a transparent ground, at its natural 77 × 52 aspect. */
export const markSvg = ({
  colorway = 'light',
  label = 'Proffplassen',
  id = 'pp',
}: MarkSvgOptions = {}): string =>
  [
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}"`,
    label === null ? ' aria-hidden="true"' : ` role="img" aria-label="${label}"`,
    '>',
    markShapes(logoColors[colorway], id),
    '</svg>',
  ].join('')

type MarkTileOptions = MarkSvgOptions & {
  /** Edge of the square viewBox. Only affects the numbers in the file. */
  size?: number
  /** Corner radius as a fraction of `size`. 0 for a hard square. */
  radius?: number
  /** `false` emits a transparent ground — for an Android adaptive foreground. */
  background?: string | false
  /** Fraction of the tile's width the mark occupies. */
  scale?: number
}

/**
 * The mark centred on a square tile. This is the favicon, the PWA icon, the iOS
 * app icon and the Android adaptive foreground.
 *
 * ⚠️ MARK ONLY, NEVER THE LOCKUP. "Proffplassen" is eleven characters; at the
 * 16 px a browser tab renders, or the 60 px an iPhone home screen does, it is a
 * grey smear. The circles survive both.
 */
export const markTileSvg = ({
  size = 64,
  radius = 0.22,
  colorway = 'dark',
  background,
  scale = markTileScale.tile,
  label = 'Proffplassen',
  id = 'pp',
}: MarkTileOptions = {}): string => {
  const palette = logoColors[colorway]
  const fill = background === undefined ? palette.ground : background
  const markWidth = size * scale
  const factor = markWidth / width
  const tx = (size - markWidth) / 2
  const ty = (size - height * factor) / 2
  const round = (value: number): string => Number(value.toFixed(3)).toString()

  return [
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${size}"`,
    label === null ? ' aria-hidden="true"' : ` role="img" aria-label="${label}"`,
    '>',
    fill === false
      ? ''
      : `<rect width="${size}" height="${size}" rx="${round(size * radius)}" fill="${fill}"/>`,
    `<g transform="translate(${round(tx)} ${round(ty)}) scale(${round(factor)})">`,
    markShapes(palette, id),
    '</g>',
    '</svg>',
  ].join('')
}
